import { useMemo, useState } from 'react'
import { format, subDays } from 'date-fns'
import { useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { ServiceRecordDialog } from '@/components/service-records/ServiceRecordDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { useDeleteServiceRecord, useServiceRecords } from '@/hooks/useServiceRecords'
import { showError } from '@/lib/error'
import type { ServiceRecord } from '@/types/service-record'
import { ServiceRecordFilterBar, type ServiceRecordFilterValue } from './components/ServiceRecordFilterBar'
import { ServiceRecordTable } from './components/ServiceRecordTable'

/** 默认近 180 天（对齐老 OA 的查询习惯） */
function defaultFilter(): ServiceRecordFilterValue {
  const end = new Date()
  const start = subDays(end, 180)
  return { scope: 'my', start_date: format(start, 'yyyy-MM-dd'), end_date: format(end, 'yyyy-MM-dd') }
}

export function ServiceRecordsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Number(searchParams.get('page')) || 1
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [filter, setFilter] = useState<ServiceRecordFilterValue>(defaultFilter)
  const [applied, setApplied] = useState<ServiceRecordFilterValue>(filter)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ServiceRecord | null>(null)

  const queryParams = useMemo(() => ({
    page, page_size: pageSize,
    service_type_id: applied.service_type_id,
    customer_id: applied.customer_id,
    user_id: applied.user_id,
    user_ids: applied.user_ids,
    start_date: applied.start_date,
    end_date: applied.end_date,
    scope: applied.scope,
    department_id: applied.department_id,
  }), [page, pageSize, applied])

  const { data, isLoading, isFetching } = useServiceRecords(queryParams)
  const deleteMutation = useDeleteServiceRecord()

  const syncPage = (next: number) => setSearchParams(params => {
    if (next <= 1) params.delete('page')
    else params.set('page', String(next))
    return params
  }, { replace: true })

  const handleSearch = () => {
    setApplied(filter)
    syncPage(1)
  }

  const handleReset = () => {
    const reset = defaultFilter()
    setFilter(reset)
    setApplied(reset)
    syncPage(1)
  }

  const handleDelete = async (r: ServiceRecord) => {
    if (!window.confirm(`确定删除「${r.subject}」这条服务记录？`)) return
    try {
      await deleteMutation.mutateAsync(r.id)
    } catch (error) {
      showError(error, '删除失败')
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">服务记录</h1>
          <p className="mt-1 text-sm text-muted-foreground">客户服务的录入、跟进与查询</p>
        </div>
        <PermissionGuard permission="service_record:create">
          <Button onClick={() => { setEditing(null); setDialogOpen(true) }}>
            <Plus className="mr-2 h-4 w-4" />录入服务
          </Button>
        </PermissionGuard>
      </div>

      <Card>
        <CardContent className="pt-6">
          <ServiceRecordFilterBar
            value={filter}
            onChange={setFilter}
            onSearch={handleSearch}
            onReset={handleReset}
            searching={isFetching}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2, 3].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <ServiceRecordTable
              records={data?.list ?? []}
              onEdit={r => { setEditing(r); setDialogOpen(true) }}
              onDelete={handleDelete}
            />
          )}
        </CardContent>
      </Card>

      <ListPagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        loading={isFetching}
        onPageChange={syncPage}
        onPageSizeChange={size => { setPageSize(size); syncPage(1) }}
      />

      <ServiceRecordDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        record={editing ?? undefined}
      />
    </div>
  )
}
